import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  ArrowRight, 
  Clock,
  CheckCircle,
  HeartHandshake,
  CreditCard,
  DollarSign,
  Package,
  Brain,
  Sparkles,
  Heart,
  Shield,
  Zap,
  TrendingUp,
  Smile,
  MapPin,
  Users,
  Calendar
} from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import CityPackagesGrid from "@/components/CityPackagesGrid";
import StoriesOfHealing from "@/components/StoriesOfHealing";
import PracticeModelInvestment from "@/components/PracticeModelInvestment";
import CalendlyInlineBooking from "@/components/CalendlyInlineBooking";
import KetamineSavingsPlan from "@/components/KetamineSavingsPlan";
import CareCreditBanner from "@/components/CareCreditBanner";

const FeesPage = () => {
  const seoOverride = usePageSeo("fees");
  const benefits = [
    {
      icon: Zap,
      title: "Rapid Relief",
      description: "Many clients experience significant improvement in symptoms within hours to days, rather than the weeks or months often required with traditional treatments.",
    },
    {
      icon: Brain,
      title: "Neuroplasticity Enhancement",
      description: "Ketamine promotes the growth of new neural connections, helping your brain form healthier thought patterns and break free from negative cycles.",
    },
    {
      icon: Shield,
      title: "Treatment-Resistant Support",
      description: "Particularly effective for those who haven't found relief with traditional antidepressants or therapy approaches.",
    },
    {
      icon: Heart,
      title: "Emotional Processing",
      description: "Creates a unique therapeutic window that allows for deeper emotional processing and healing of trauma.",
    },
    {
      icon: Sparkles,
      title: "Reduced Anxiety & Depression",
      description: "Clinically shown to significantly reduce symptoms of depression, anxiety, PTSD, and other mood disorders.",
    },
    {
      icon: HeartHandshake,
      title: "Enhanced Therapy Sessions",
      description: "When combined with psychotherapy, ketamine can deepen therapeutic insights and accelerate the healing process.",
    },
  ];

  const outcomes = [
    {
      title: "Sustained Mood Improvement",
      description: "Many clients report lasting improvements in mood, outlook, and emotional regulation that continue well beyond the treatment period.",
    },
    {
      title: "Breaking Negative Thought Patterns",
      description: "The neuroplasticity effects help create new, healthier neural pathways, making it easier to maintain positive changes long-term.",
    },
    {
      title: "Improved Relationships",
      description: "As symptoms decrease and emotional capacity increases, many clients experience significant improvements in their personal and professional relationships.",
    },
    {
      title: "Greater Resilience",
      description: "Clients often develop stronger coping mechanisms and emotional resilience that serve them throughout life's challenges.",
    },
    {
      title: "Reconnection with Self",
      description: "Many report a renewed sense of purpose, creativity, and connection with themselves and others.",
    },
    {
      title: "Reduced Medication Dependence",
      description: "Some clients are able to reduce or eliminate other psychiatric medications under their provider's guidance.",
    },
  ];

  return (
    <div className="pt-20" data-testid="fees-page">
      <SEO
        title="Psychotherapy Intensive Options"
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        description={seoOverride?.meta_description || "Three Ketamine-Assisted Psychotherapy intensives — 8 Weeks Foundation ($3,000), 20 Weeks All-In ($9,000), and 12 Weeks Continuation ($6,000). Save $100/hr vs. hourly rate. Payment plans and out-of-network insurance filing available."}
        imageAlt={seoOverride?.image_alt}
        pathname="/fees"
      />
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-8"
          >
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium"
              data-testid="fees-location-pill"
            >
              <MapPin className="h-4 w-4" />
              For FL, ME, DC Residents — fully online
            </span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Laura's portrait */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2 flex justify-center lg:justify-start"
            >
              <div className="relative">
                <div className="absolute -inset-3 rounded-3xl bg-primary/10 -z-10"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/l4ja8zkq_IMG_4558_Facetune_17-01-2023-21-22-22.jpeg"
                  alt="Laura A. Jeanis, LCSW, LICSW, CCTP, CEO — Licensed Psychotherapist offering Ketamine-Assisted Therapy Intensives for hyper-independent women in FL & ME"
                  className="rounded-3xl shadow-soft object-cover w-full max-w-md aspect-[4/5]"
                  data-testid="fees-laura-portrait"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="lg:col-span-3"
            >
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-tight">
                Virtual Ketamine-Assisted Therapy Intensives for Hyper-Independent Women in{" "}
                <span className="italic text-primary">FL, ME, &amp; DC</span>
              </h1>
              <h2 className="mt-4 font-serif text-xl sm:text-2xl lg:text-3xl italic font-medium text-primary leading-snug">
                Invest in Your Healing Journey
              </h2>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                Investing in Ketamine-Assisted Therapy can bring deeply transformative benefits to your relationships.
              </p>
              <div
                className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5 rounded-2xl border border-primary/30 bg-primary/5 px-5 py-4 max-w-2xl"
                data-testid="fees-payment-plan-callout"
              >
                <div className="flex items-start gap-4 flex-1">
                  <CreditCard className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm uppercase tracking-[0.18em] text-primary font-semibold">
                      Extended Payment Plans Available
                    </p>
                    <p className="mt-1 font-serif text-lg md:text-xl text-foreground">
                      For as low as <span className="font-semibold text-primary">$125 bi-weekly</span>.
                    </p>
                  </div>
                </div>
                <a
                  href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto flex-shrink-0"
                >
                  <Button
                    className="w-full sm:w-auto rounded-full px-6 py-5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="fees-payment-plan-cta"
                  >
                    Book Free 40-min Call
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Practice Model & Investment */}
      <PracticeModelInvestment background="bg-white" />

      {/* CareCredit Financing Banner — directly under Out-of-Network Reimbursement */}
      <CareCreditBanner variant="muted" />

      {/* Session Packages */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-3 py-1 bg-accent/15 text-accent text-xs font-medium rounded-full uppercase tracking-wide">
              Session Intensives
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mt-4">
              Intensives For Your <span className="italic text-primary">Unique Ketamine Journey</span>
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the container that best matches where you are in your transformation.
            </p>
          </motion.div>

          {/* Free Consultation CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12 max-w-3xl mx-auto"
            data-testid="free-consultation-banner"
          >
            <Card className="p-6 bg-white border-primary/30 rounded-2xl flex flex-col sm:flex-row items-center gap-4 justify-between">
              <div className="flex items-center gap-4 text-center sm:text-left">
                <div className="p-3 rounded-full bg-primary/10 flex-shrink-0">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-semibold text-foreground">Not sure yet? Start with a Free 40-Minute Consultation</h3>
                  <p className="text-sm text-muted-foreground">A no-pressure call to see which intensive is right for you.</p>
                </div>
              </div>
              <a
                href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap" data-testid="book-free-consult-btn">
                  Book Free Call
                </Button>
              </a>
            </Card>
          </motion.div>

          {/* Three Packages */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Package 1: 8 Weeks Transformational Intensive (Foundation) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              data-testid="package-8-weeks"
            >
              <Card className="p-8 h-full bg-white border-border/40 rounded-3xl flex flex-col relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-accent" />
                <div className="mb-6">
                  <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                    Foundation
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-foreground mt-3">
                    8 Weeks Transformational Intensive
                  </h3>
                  <p className="text-sm text-muted-foreground italic mt-3 leading-relaxed">
                    For the woman who knows she cannot go another day without change — and knows that this change must first come from within.
                  </p>
                </div>

                <ul className="space-y-3 text-sm text-foreground/90 mb-6 flex-grow">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>1 Evaluation session</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>2 Treatment planning sessions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>1 Preparation session</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>2 Ketamine dosing sessions <span className="text-muted-foreground">(1× bi-weekly · 2 hours each)</span></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>4 Integration sessions <span className="text-muted-foreground">(1× weekly)</span></span>
                  </li>
                </ul>

                <div className="pt-6 border-t border-border/40">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">10 Individual Sessions ~ 12 Meeting Hours</p>
                  <p className="text-xs text-muted-foreground mt-2">Payment plans from $125 bi-weekly</p>
                  <p className="text-xs text-muted-foreground mt-1">Files out-of-network claims for insurance reimbursement</p>
                  <a
                    href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-5"
                  >
                    <Button className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="package-1-cta">
                      Schedule a Consultation
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </Card>
            </motion.div>

            {/* Package 2: 20 Weeks Advanced Inner Transformation (All-In) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              data-testid="package-20-weeks"
            >
              <Card className="p-8 h-full bg-primary text-primary-foreground rounded-3xl flex flex-col relative overflow-hidden">
                <div className="absolute top-3 right-3">
                  <span className="inline-block px-3 py-1 bg-white/15 text-primary-foreground text-xs font-medium rounded-full border border-white/30">
                    Most Comprehensive
                  </span>
                </div>
                <div className="mb-6">
                  <span className="inline-block px-3 py-1 bg-white/10 text-primary-foreground text-xs font-medium rounded-full">
                    All-In
                  </span>
                  <h3 className="font-serif text-2xl font-semibold mt-3">
                    Advanced Inner Transformation
                  </h3>
                  <p className="text-sm opacity-80 mt-1">20 Weeks Container</p>
                  <p className="text-sm italic mt-3 leading-relaxed opacity-90">
                    For the woman who is ready to go all in — right now.
                  </p>
                </div>

                <ul className="space-y-3 text-sm mb-6 flex-grow">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>1 Evaluation session</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>2 Treatment planning sessions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>1 Preparation session</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>8 Ketamine dosing sessions <span className="opacity-75">(2 hrs each · 1× bi-weekly)</span></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>16 Integration sessions <span className="opacity-75">(1 hr each · 1× weekly)</span></span>
                  </li>
                </ul>

                <div className="pt-6 border-t border-white/20">
                  <p className="text-xs uppercase tracking-wide opacity-75 font-medium">28 Individual Sessions ~ 36 Meeting Hours</p>
                  <p className="text-xs opacity-75 mt-2">Payment plans from $125 bi-weekly</p>
                  <p className="text-xs opacity-75 mt-1">Files out-of-network claims for insurance reimbursement</p>
                  <a
                    href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-5"
                  >
                    <Button className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="package-2-cta">
                      Schedule a Consultation
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </Card>
            </motion.div>

            {/* Package 3: 12 Weeks Transformational Intensive (Continuation) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              data-testid="package-12-weeks"
            >
              <Card className="p-8 h-full bg-white border-border/40 rounded-3xl flex flex-col relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-accent to-primary" />
                <div className="mb-6">
                  <span className="inline-block px-3 py-1 bg-accent/15 text-accent text-xs font-medium rounded-full">
                    Continuation
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-foreground mt-3">
                    12 Weeks Transformational Intensive
                  </h3>
                  <p className="text-sm text-muted-foreground italic mt-3 leading-relaxed">
                    For the woman who has already begun this journey and knows she must see it through — all the way.
                  </p>
                  <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg mt-3 inline-block">
                    Available only after completing the 8 Weeks Intensive
                  </p>
                </div>

                <ul className="space-y-3 text-sm text-foreground/90 mb-6 flex-grow">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>6 Ketamine dosing sessions <span className="text-muted-foreground">(2 hrs each · 1× bi-weekly)</span></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>12 Integration sessions <span className="text-muted-foreground">(1 hr each · 1× weekly)</span></span>
                  </li>
                </ul>

                <div className="pt-6 border-t border-border/40">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">18 Individual Sessions ~ 24 Meeting Hours</p>
                  <p className="text-xs text-muted-foreground mt-2">Payment plans from $125 bi-weekly</p>
                  <p className="text-xs text-muted-foreground mt-1">Files out-of-network claims for insurance reimbursement</p>
                  <a
                    href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-5"
                  >
                    <Button className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="package-3-cta">
                      Schedule a Consultation
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Comprehensive DC-Style Package — Featured full-width card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="mt-10"
            data-testid="package-comprehensive-16-weeks"
          >
            <Card className="p-8 md:p-12 bg-white border border-primary/25 rounded-3xl shadow-soft relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-accent" />
              {/* Recommendation ribbon */}
              <div className="absolute top-4 right-4 z-10" data-testid="package-comprehensive-ribbon">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-[11px] font-semibold rounded-full shadow-md uppercase tracking-wide">
                  <Sparkles className="h-3 w-3" />
                  Recommended for first-time KAP clients
                </span>
              </div>
              <div className="flex items-start justify-between flex-wrap gap-4 mb-6 pt-8 sm:pt-4">
                <div className="max-w-2xl">
                  <span className="inline-block px-3 py-1 bg-accent/15 text-accent text-xs font-semibold rounded-full uppercase tracking-wide">
                    Most Structured
                  </span>
                  <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground mt-3">
                    16 Weeks All-In Intensive
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground italic mt-3 leading-relaxed">
                    For the hyper-independent woman ready to lay her armor down and feel safe in her relationships.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Payment plans from $125 bi-weekly</p>
                </div>
              </div>

              <ul className="space-y-3 text-sm md:text-base text-foreground/90 mb-8" data-testid="package-comprehensive-items">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Initial Psychiatric Diagnostic Evaluation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>(2) 1-hour treatment planning sessions</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>(1) 1-hour preparation session</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>(6) 2-hour ketamine dosing sessions</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>(12) 1-hour processing &amp; integration sessions</span>
                </li>
              </ul>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-border/40">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-accent/15 flex-shrink-0">
                    <Clock className="h-4 w-4 text-accent" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Clinical Hours</p>
                    <p className="font-serif text-lg font-semibold text-foreground">29 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-accent/15 flex-shrink-0">
                    <Calendar className="h-4 w-4 text-accent" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Commitment</p>
                    <p className="font-serif text-lg font-semibold text-foreground">16 weeks</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-6">
                Files out-of-network claims for insurance reimbursement. All payments processed through HIPAA-compliant billing.
              </p>

              <a
                href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-6"
              >
                <Button
                  className="w-full sm:w-auto rounded-full py-6 px-8 text-base bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="package-comprehensive-cta"
                >
                  Schedule a Free 40-Minute Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </Card>
          </motion.div>

          {/* Group Immersion callout — softer, more affordable entry option */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 }}
            viewport={{ once: true }}
            className="mt-8 max-w-4xl mx-auto"
            data-testid="fees-group-immersion-link"
          >
            <Link
              to="/specialties/ketamine-therapy-group-immersion-fl-me"
              className="group block"
            >
              <div className="rounded-3xl bg-white border-2 border-accent/40 hover:border-accent/70 hover:shadow-hover transition-all p-6 md:p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-accent to-primary" />
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex-1 min-w-[240px]">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-accent/15 text-accent text-xs font-semibold rounded-full uppercase tracking-wide">
                      <Users className="h-3.5 w-3.5" />
                      New · Group Immersion
                    </span>
                    <h3 className="font-serif text-xl md:text-2xl font-semibold text-foreground mt-3">
                      Prefer to heal in circle? Try the 9-Week Group Immersion
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      14.5 clinical hours · 6 extended group sessions + 3 individual sessions · max 3 members · available for residents of FL &amp; ME.
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-muted-foreground">Payment plans from $125 bi-weekly</p>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-primary font-medium text-sm group-hover:gap-2.5 transition-all">
                      See the intensive
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Package savings callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            viewport={{ once: true }}
            className="mt-8 max-w-4xl mx-auto"
            data-testid="package-savings-callout"
          >
            <div className="rounded-2xl bg-accent/10 border border-accent/30 p-5 flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/90 leading-relaxed">
                <span className="font-semibold text-foreground">Save $100 per session hour</span>{" "}
                when you purchase a psychotherapy intensive vs. paying the full rate of{" "}
                <span className="font-semibold">$350 per session hour</span>.
              </p>
            </div>
          </motion.div>

          {/* City-specific packages */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-12"
          >
            <CityPackagesGrid
              eyebrow="Where do you live?"
              heading="City-Specific Intensives for Hyper-Independent Women"
              subheading="Tailored offerings for the state where you're licensed to receive care."
              testIdPrefix="city-specific-packages"
            />
          </motion.div>

          {/* Additional Hourly Sessions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            viewport={{ once: true }}
            className="mt-10"
            data-testid="hourly-sessions-callout"
          >
            <Card className="p-8 bg-white border-accent/30 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-accent/20 flex-shrink-0">
                  <Clock className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-serif text-xl font-semibold text-foreground">
                    Additional Processing Sessions are Available at an Hourly Rate of $350
                  </h3>
                  <p className="text-muted-foreground mt-2 leading-relaxed">
                    Once your intensive wraps, you can continue your healing journey by booking additional integration and processing sessions at an hourly rate of $350.
                    Contact Laura to discuss your continued care.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Payment Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 max-w-3xl mx-auto"
          >
            <Card className="p-6 bg-white/80 border-border/40 rounded-xl">
              <div className="flex items-start gap-4">
                <CreditCard className="h-6 w-6 text-primary flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-foreground">Payment Methods</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    I accept all cards as a form of payment. Payments are made through HIPAA-compliant billing services.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Benefits of Ketamine-Assisted Psychotherapy
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover how KAP can transform your mental health and well-being.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-8 h-full bg-white border-0 rounded-2xl shadow-sm hover:shadow-soft transition-shadow">
                  <div className="p-3 rounded-full bg-primary/10 w-fit mb-4">
                    <benefit.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lasting Outcomes Section */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <TrendingUp className="h-6 w-6 text-accent" />
              <span className="text-accent font-medium">Long-Term Results</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Lasting Positive Outcomes
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              The benefits of Ketamine-Assisted Psychotherapy extend far beyond the treatment sessions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {outcomes.map((outcome, index) => (
              <motion.div
                key={outcome.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 h-full bg-secondary/30 border-0 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Smile className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">{outcome.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {outcome.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stories of Healing */}
      <StoriesOfHealing />

      {/* Ketamine Intensive Savings Plan */}
      <KetamineSavingsPlan background="bg-white" />

      {/* Inline Calendly Booking Widget */}
      <CalendlyInlineBooking background="bg-secondary/30" />

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Ready to Begin Your Transformation?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
              Schedule a free consultation to learn how Ketamine-Assisted Psychotherapy 
              can help you achieve lasting mental wellness.
            </p>
            <div className="mt-10">
              <a href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting" target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="rounded-full px-10 py-6 text-lg btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="fees-cta"
                >
                  Schedule Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FeesPage;
